import type { ComponentType, HTMLAttributes, ReactNode } from 'react';
import { KIND_ICON, NOTICE_CLS, type NoticeTone } from './Toast';

/**
 * #664 (F67, bug 76) — the inline message: a refusal under a field, a warning
 * in a modal, the success of a resend. One tone gives one colour (`NOTICE_CLS`
 * in `Toast.tsx`, beside the toast kinds) and one role:
 *
 *   error   role="alert"   a screen reader reads it at once
 *   other   role="status"  a screen reader reads it when it is free
 *
 * `inlineError(err)` stays the producer of the text of a refusal.
 *
 * `plain` draws the text with no box (the refusal line under a field).
 * `icon` draws the SVG of the tone before the text, or the icon that it
 * gets (`icon={WarningIcon}`). `className` is for the
 * place of the notice (`mb-3`, `mt-1`). The padding comes from the box, never
 * from a `className`: two padding utilities on one element have no sure
 * winner. Every `data-*` attribute and `data-testid` goes to the element.
 */
const SIZE_CLS = { xs: 'text-xs', sm: 'text-sm' } as const;

interface NoticeProps extends Omit<HTMLAttributes<HTMLDivElement>, 'className' | 'children'> {
  tone: NoticeTone;
  plain?: boolean;
  size?: keyof typeof SIZE_CLS;
  icon?: boolean | ComponentType<{ className?: string }>;
  className?: string;
  children: ReactNode;
}

export default function Notice({ tone, plain = false, size = 'sm', icon = false, className = '', role, children, ...rest }: NoticeProps) {
  const cls = NOTICE_CLS[tone];
  const Icon = typeof icon === 'function' ? icon : icon && tone !== 'neutral' ? KIND_ICON[tone] : null;
  const classes = [
    SIZE_CLS[size],
    cls.text,
    plain ? '' : `${cls.box} px-3 py-2 rounded-lg`,
    Icon ? 'flex items-start gap-1.5' : '',
    className,
  ].filter(Boolean).join(' ');
  return (
    <div {...rest} role={role ?? (tone === 'error' ? 'alert' : 'status')} className={classes}>
      {Icon ? (
        <>
          <Icon className="w-4 h-4 shrink-0 mt-0.5" />
          <span className="min-w-0">{children}</span>
        </>
      ) : children}
    </div>
  );
}
